import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import api from '../utils/api';

const Contact = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguageCurrency();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const messageTextareaRef = useRef(null);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    
    // Auto-resize textarea
    if (name === 'message' && messageTextareaRef.current) {
      const textarea = messageTextareaRef.current;
      textarea.style.height = 'auto'; // Reset height to get the correct scrollHeight
      const scrollHeight = textarea.scrollHeight;
      const baseHeight = 48; // Base height matching input fields
      const newHeight = Math.max(baseHeight, scrollHeight);
      textarea.style.height = `${newHeight}px`;
    }
  };
  
  // Set initial textarea height on mount
  useEffect(() => {
    if (messageTextareaRef.current) {
      const textarea = messageTextareaRef.current;
      const baseHeight = 48; // Match input field height
      textarea.style.height = `${baseHeight}px`;
      textarea.style.minHeight = `${baseHeight}px`;
    }
  }, []);

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = '';

    switch (name) {
      case 'email':
        if (!value) {
          error = t('common.emailRequired');
        } else if (!validateEmail(value)) {
          error = t('common.emailInvalid');
        }
        break;
      case 'phone':
        if (value && !validatePhone(value)) {
          error = t('common.phoneInvalid');
        }
        break;
      case 'name':
        if (!value.trim()) {
          error = t('common.nameRequired');
        }
        break;
      case 'inquiryType':
        if (!value) {
          error = t('common.inquiryTypeRequired');
        }
        break;
      case 'subject':
        if (!value.trim()) {
          error = t('common.subjectRequired');
        }
        break;
      case 'message':
        if (!value.trim()) {
          error = t('common.messageRequired');
        }
        break;
      default:
        break;
    }

    if (error) {
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = t('common.nameRequired');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('common.emailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('common.emailInvalid');
    }
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = t('common.phoneInvalid');
    }
    if (!formData.inquiryType) {
      newErrors.inquiryType = t('common.inquiryTypeRequired');
    }
    if (!formData.subject.trim()) {
      newErrors.subject = t('common.subjectRequired');
    }
    if (!formData.message.trim()) {
      newErrors.message = t('common.messageRequired');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await api.post('/api/contact', formData);
      if (response.data?.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          inquiryType: '',
          subject: '',
          message: '',
        });
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus(null), 5000);
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || t('contact.errorGeneric');
      setSubmitStatus('error');
      setErrors((prev) => ({ ...prev, _submit: message }));
      setTimeout(() => {
        setSubmitStatus(null);
        setErrors((prev) => ({ ...prev, _submit: '' }));
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
      <Navbar />
      <WhatsAppButton />

      {/* Hero Section with Gradient Background */}
      <section 
        className="pt-32 pb-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
        style={{ 
          background: 'linear-gradient(180deg, rgba(29, 42, 59, 0.98) 0%, rgba(29, 42, 59, 0.96) 30%, rgba(29, 42, 59, 0.95) 60%, rgba(29, 42, 59, 0.95) 100%)',
          width: '100%',
          minHeight: 'auto',
          position: 'relative'
        }}
      >
        {/* Subtle Gold Glow at Top */}
        <div className="absolute top-0 left-0 right-0 h-1/3 opacity-30" style={{
          background: 'linear-gradient(180deg, rgba(213, 181, 119, 0.15) 0%, rgba(213, 181, 119, 0.05) 50%, transparent 100%)',
          pointerEvents: 'none'
        }}></div>
        
        {/* Subtle Decorative Elements - Reduced */}
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full opacity-3" style={{ 
          background: 'radial-gradient(circle, rgba(213, 181, 119, 0.2) 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
          filter: 'blur(50px)'
        }}></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-3" style={{ 
          background: 'radial-gradient(circle, rgba(213, 181, 119, 0.15) 0%, transparent 70%)',
          transform: 'translate(-30%, 30%)',
          filter: 'blur(50px)'
        }}></div>

        <div className="w-full max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <div className="inline-block mb-6">
              <div className="w-20 h-1 mx-auto mb-3 rounded-full animate-line-expand" style={{ 
                background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
                boxShadow: '0 0 10px rgba(213, 181, 119, 0.5)'
              }}></div>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight relative flex items-center justify-center flex-wrap gap-2 sm:gap-4" style={{ 
              textShadow: '0 0 30px rgba(213, 181, 119, 0.3), 0 4px 8px rgba(0, 0, 0, 0.3)',
              fontSize: 'clamp(3.5rem, 7vw, 6rem)'
            }}>
              <span className="relative inline-flex items-center justify-center gap-2 sm:gap-4">
                {/* Contact Us Icon - Mail/Phone Icon */}
                <svg 
                  className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 animate-icon-float"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  style={{
                    color: '#D5B577',
                    filter: 'drop-shadow(0 0 10px rgba(213, 181, 119, 0.5))'
                  }}
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
                  />
                </svg>
                {/* Animated Text */}
                <span className="contact-us-text" style={{ fontSize: 'inherit' }}>
                  {language === 'ar' ? (
                    <span
                      style={{
                        background: 'linear-gradient(135deg, #D5B577 0%, #f5d89a 50%, #D5B577 100%)',
                        backgroundSize: '200% 200%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'gradient-shift-once 3s ease-in-out forwards',
                        fontSize: 'inherit'
                      }}
                    >
                      {t('contact.title')}
                    </span>
                  ) : (
                    t('contact.title').split('').map((char, index) => (
                      <span
                        key={index}
                        className="inline-block"
                        style={{
                          animationDelay: `${index * 0.08}s`,
                          background: 'linear-gradient(135deg, #D5B577 0%, #f5d89a 50%, #D5B577 100%)',
                          backgroundSize: '200% 200%',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          animation: 'gradient-shift-once 3s ease-in-out forwards',
                          fontSize: 'inherit'
                        }}
                      >
                        {char === ' ' ? '\u00A0' : char}
                      </span>
                    ))
                  )}
                </span>
                {/* Animated underline */}
                <div className="absolute -bottom-2 left-0 right-0 h-0.5 animate-underline-expand" style={{
                  background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
                  opacity: 0.8
                }}></div>
                {/* Glow effect */}
                <div className="absolute inset-0 animate-pulse-glow" style={{
                  background: 'radial-gradient(circle, rgba(213, 181, 119, 0.2) 0%, transparent 70%)',
                  filter: 'blur(20px)',
                  zIndex: -1
                }}></div>
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-2xl mx-auto leading-relaxed font-light animate-fade-in-up" style={{
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              animationDelay: '0.8s',
              animationFillMode: 'both'
            }}>
              {t('contact.subtitle')}
            </p>
            <div className="w-20 h-1 mx-auto mt-6 rounded-full animate-line-expand-delayed" style={{ 
              background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
              boxShadow: '0 0 10px rgba(213, 181, 119, 0.5)'
            }}></div>
          </div>
        </div>
      </section>

      {/* Contact Information Section - Professional Layout */}
      <section className="pt-20 pb-8 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-32 -mt-12 relative z-20" style={{ width: '100%', minHeight: 'auto' }}>
        <div className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              {t('contact.contactInfo')}
            </h2>
            <p className="text-gray-400 text-lg">
              {t('contact.subtitle')}
            </p>
          </div>
          
          {/* Professional Table-like Structure */}
          <div className="bg-slate-800/50 rounded-xl border border-amber-500/20 overflow-hidden">
            <div className="divide-y divide-gray-700/50">
              {/* Email Row */}
              <div 
                className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6 p-6 sm:p-8 hover:bg-slate-700/30 transition-colors duration-200 cursor-pointer group"
                onClick={() => window.location.href = 'mailto:info@hostaglobal.com'}
              >
                <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)' }}>
                  <svg className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1 text-center sm:text-left w-full sm:w-auto">
                  <div className="text-sm text-gray-400 mb-1 font-medium">{t('contact.email')}</div>
                  <a href="mailto:info@hostaglobal.com" className="text-lg sm:text-xl text-white hover:text-[#D5B577] transition-colors font-semibold">
                    info@hostaglobal.com
                  </a>
                </div>
              </div>

              {/* Phone Row */}
              <div 
                className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6 p-6 sm:p-8 hover:bg-slate-700/30 transition-colors duration-200 cursor-pointer group"
                onClick={() => window.location.href = 'tel:+201234567890'}
              >
                <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)' }}>
                  <svg className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div className="flex-1 text-center sm:text-left w-full sm:w-auto">
                  <div className="text-sm text-gray-400 mb-1 font-medium">{t('contact.phone')}</div>
                  <a href="tel:+201234567890" className="text-lg sm:text-xl text-white hover:text-[#D5B577] transition-colors font-semibold">
                  +20 123 456 7890
                  </a>
                </div>
              </div>

              {/* Address Row */}
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6 p-6 sm:p-8 hover:bg-slate-700/30 transition-colors duration-200 group">
                <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)' }}>
                  <svg className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="flex-1 text-center sm:text-left w-full sm:w-auto">
                  <div className="text-sm text-gray-400 mb-1 font-medium">{t('common.address')}</div>
                  <div className="text-lg sm:text-xl text-white font-semibold">
                    {t('contact.address')}
                  </div>
                </div>
              </div>

              {/* Business Hours Row */}
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6 p-6 sm:p-8 hover:bg-slate-700/30 transition-colors duration-200 group">
                <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)' }}>
                  <svg className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 text-center sm:text-left w-full sm:w-auto">
                  <div className="text-sm text-gray-400 mb-1 font-medium">{t('contact.workingHours')}</div>
                  <div className="text-lg sm:text-xl text-white font-semibold">
                    {t('common.monFri')}<br className="sm:hidden" />
                    <span className="hidden sm:inline"> • </span>{t('common.sat')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section - Centered */}
      <section 
        className="pt-10 pb-20 sm:pb-32 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-32 relative sm:-mt-[120px] sm:pt-[140px]" 
        style={{ 
          width: '100%', 
          minHeight: 'auto',
          backgroundColor: '#1D2A3B',
          zIndex: 10
        }}
      >
        <div className="w-full max-w-3xl mx-auto">
          <div 
            className="p-4 sm:p-6 lg:p-10 rounded-2xl sm:rounded-3xl bg-white/5 backdrop-blur-xl sm:backdrop-blur-none"
            style={{ 
              border: '1px solid rgba(213, 181, 119, 0.3)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            }}
          >
            <div className="mb-6 sm:mb-10 text-center">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">
                {t('contact.sendMessage')}
              </h2>
              <p className="text-gray-400 text-sm sm:text-base md:text-lg">
                {t('common.fillFormBelow')}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-xs sm:text-sm md:text-base font-semibold md:font-bold mb-2 md:mb-3" style={{ color: '#D5B577' }}>
                    {t('contact.name')} <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none z-10">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={t('contact.name')}
                      className="w-full pl-10 sm:pl-12 md:pl-14 lg:pl-16 pr-3 sm:pr-4 md:pr-5 rounded-lg sm:rounded-xl md:rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 text-sm sm:text-base md:text-lg text-white placeholder-gray-400"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        borderColor: errors.name ? '#ef4444' : 'rgba(213, 181, 119, 0.3)',
                        color: '#fff',
                        height: '48px',
                        minHeight: '48px',
                        boxSizing: 'border-box',
                        paddingTop: '0',
                        paddingBottom: '0',
                        lineHeight: '48px',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#D5B577';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(213, 181, 119, 0.1)';
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (!errors.name) {
                          e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                          e.target.style.boxShadow = 'none';
                        }
                      }}
                    />
                  </div>
                  {errors.name && (
                    <p className="mt-1 md:mt-2 text-sm text-red-400">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs sm:text-sm md:text-base font-semibold md:font-bold mb-2 md:mb-3" style={{ color: '#D5B577' }}>
                    {t('contact.email')} <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none z-10">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={t('common.enterEmail')}
                      className="w-full pl-10 sm:pl-12 md:pl-14 lg:pl-16 pr-3 sm:pr-4 md:pr-5 rounded-lg sm:rounded-xl md:rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 text-sm sm:text-base md:text-lg text-white placeholder-gray-400"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        borderColor: errors.email ? '#ef4444' : 'rgba(213, 181, 119, 0.3)',
                        color: '#fff',
                        height: '48px',
                        minHeight: '48px',
                        boxSizing: 'border-box',
                        paddingTop: '0',
                        paddingBottom: '0',
                        lineHeight: '48px',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#D5B577';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(213, 181, 119, 0.1)';
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (!errors.email) {
                          e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                          e.target.style.boxShadow = 'none';
                        }
                      }}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 md:mt-2 text-sm text-red-400">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs sm:text-sm md:text-base font-semibold md:font-bold mb-2 md:mb-3" style={{ color: '#D5B577' }}>
                    {t('contact.phone')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none z-10">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={t('common.enterPhone')}
                      dir="ltr"
                      className="w-full pl-10 sm:pl-12 md:pl-14 lg:pl-16 pr-3 sm:pr-4 md:pr-5 rounded-lg sm:rounded-xl md:rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 text-sm sm:text-base md:text-lg text-white placeholder-gray-400"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        borderColor: errors.phone ? '#ef4444' : 'rgba(213, 181, 119, 0.3)',
                        color: '#fff',
                        height: '48px',
                        minHeight: '48px',
                        boxSizing: 'border-box',
                        paddingTop: '0',
                        paddingBottom: '0',
                        lineHeight: '48px',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#D5B577';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(213, 181, 119, 0.1)';
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (!errors.phone) {
                          e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                          e.target.style.boxShadow = 'none';
                        }
                      }}
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 md:mt-2 text-sm text-red-400">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="inquiryType" className="block text-xs sm:text-sm md:text-base font-semibold md:font-bold mb-2 md:mb-3" style={{ color: '#D5B577' }}>
                    {t('common.typeOfInquiry')} <span className="text-red-400">*</span>
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-4 rounded-lg sm:rounded-xl md:rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 text-sm sm:text-base md:text-lg"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      borderColor: errors.inquiryType ? '#ef4444' : 'rgba(213, 181, 119, 0.3)',
                      color: formData.inquiryType ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                      height: '48px',
                      minHeight: '48px',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#D5B577';
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(213, 181, 119, 0.1)';
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!errors.inquiryType) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <option value="">{t('common.selectInquiryType')}</option>
                    <option value="general">{t('common.inquiryTypeGeneral')}</option>
                    <option value="booking">{t('common.inquiryTypeBooking')}</option>
                    <option value="property">{t('common.inquiryTypeProperty')}</option>
                    <option value="partnership">{t('common.inquiryTypePartnership')}</option>
                    <option value="support">{t('common.inquiryTypeSupport')}</option>
                    <option value="other">{t('common.inquiryTypeOther')}</option>
                  </select>
                  {errors.inquiryType && (
                    <p className="mt-1 md:mt-2 text-sm text-red-400">{errors.inquiryType}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="subject" className="block text-xs sm:text-sm md:text-base font-semibold md:font-bold mb-2 md:mb-3" style={{ color: '#D5B577' }}>
                    {t('common.subject')} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={t('common.enterSubject')}
                    className="w-full px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-4 rounded-lg sm:rounded-xl md:rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 text-sm sm:text-base md:text-lg text-white placeholder-gray-400"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      borderColor: errors.subject ? '#ef4444' : 'rgba(213, 181, 119, 0.3)',
                      color: '#fff',
                      height: '48px',
                      minHeight: '48px',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#D5B577';
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(213, 181, 119, 0.1)';
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!errors.subject) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  />
                  {errors.subject && (
                    <p className="mt-1 md:mt-2 text-sm text-red-400">{errors.subject}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs sm:text-sm md:text-base font-semibold md:font-bold mb-2 md:mb-3" style={{ color: '#D5B577' }}>
                    {t('contact.message')} <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    ref={messageTextareaRef}
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={t('common.enterMessage')}
                    className="w-full px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-4 rounded-lg sm:rounded-xl md:rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2 resize-none text-sm sm:text-base md:text-lg text-white placeholder-gray-400 overflow-hidden"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      borderColor: errors.message ? '#ef4444' : 'rgba(213, 181, 119, 0.3)',
                      color: '#fff',
                      minHeight: '48px',
                      height: '48px',
                      boxSizing: 'border-box',
                      lineHeight: '1.5',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#D5B577';
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(213, 181, 119, 0.1)';
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!errors.message) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  />
                  {errors.message && (
                    <p className="mt-1 md:mt-2 text-sm text-red-400">{errors.message}</p>
                  )}
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                    <p className="text-green-400">{t('common.messageSentSuccess')}</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                    <p className="text-red-400">{errors._submit || t('common.messageSentError')}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 sm:py-4 md:py-5 px-6 sm:px-8 rounded-lg sm:rounded-xl md:rounded-xl font-semibold md:font-bold text-sm sm:text-base md:text-lg transition-all duration-200 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#D5B577', color: '#1D2A3B', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.target.style.backgroundColor = '#c4a366';
                      e.target.style.transform = 'translateY(-4px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) {
                      e.target.style.backgroundColor = '#D5B577';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {isSubmitting ? t('common.loading') : t('contact.send')}
                </button>
              </form>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="pt-8 sm:pt-12 pb-20 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-32" style={{ width: '100%', minHeight: 'auto' }}>
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              {t('contact.findUsOnMap')}
            </h2>
            <p className="text-xl text-gray-300">
              {t('common.visitOffice')}
            </p>
          </div>
          
          <div className="rounded-2xl overflow-hidden border-2" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>


              <iframe
                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3455.428618705283!2d31.416542875551848!3d29.995846774948074!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjnCsDU5JzQ1LjEiTiAzMcKwMjUnMDguOCJF!5e0!3m2!1sen!2seg!4v1772237707307!5m2!1sen!2seg"
                width="100%"
                height="350"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              title="HOSTA GLOBAL Office Location"
          />
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Contact;

// Add CSS animations
if (typeof document !== 'undefined') {
  const styleId = 'contact-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes gradient-shift {
        0%, 100% { 
          background-position: 0% 50%;
          transform: translateY(0);
        }
        50% { 
          background-position: 100% 50%;
          transform: translateY(-3px);
        }
      }
      
      @keyframes gradient-shift-once {
        0% { 
          background-position: 0% 50%;
          transform: translateY(0);
          opacity: 0;
        }
        50% { 
          background-position: 100% 50%;
          transform: translateY(-3px);
          opacity: 1;
        }
        100% { 
          background-position: 0% 50%;
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes underline-expand {
        0% { 
          width: 0;
          opacity: 0;
        }
        50% {
          opacity: 0.8;
        }
        100% { 
          width: 100%;
          opacity: 0.8;
        }
      }
      
      @keyframes pulse-glow {
        0%, 100% { 
          opacity: 0.3;
          transform: scale(1);
        }
        50% { 
          opacity: 0.6;
          transform: scale(1.1);
        }
      }
      
      @keyframes line-expand {
        0% { 
          width: 0;
          opacity: 0;
        }
        100% { 
          width: 80px;
          opacity: 1;
        }
      }
      
      @keyframes line-expand-delayed {
        0% { 
          width: 0;
          opacity: 0;
        }
        50% {
          opacity: 0;
        }
        100% { 
          width: 80px;
          opacity: 1;
        }
      }
      
      @keyframes fade-in-up {
        0% { 
          opacity: 0;
          transform: translateY(20px);
        }
        100% { 
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes icon-float {
        0%, 100% { 
          transform: translateY(0) rotate(0deg);
        }
        50% { 
          transform: translateY(-8px) rotate(5deg);
        }
      }
      
      @keyframes sparkle {
        0%, 100% { 
          opacity: 0.3;
          transform: scale(1);
        }
        50% { 
          opacity: 1;
          transform: scale(1.5);
        }
      }
      
      .contact-us-text span {
        animation: letter-float-once 2s ease-out forwards;
      }
      
      @keyframes letter-float-once {
        0% { 
          transform: translateY(20px) rotate(0deg);
          opacity: 0;
        }
        100% { 
          transform: translateY(0) rotate(0deg);
          opacity: 1;
        }
      }
      
      .animate-line-expand {
        animation: line-expand 1.5s ease-out forwards;
      }
      
      .animate-underline-expand {
        animation: underline-expand 1.2s ease-out 0.5s forwards;
      }
      
      .animate-pulse-glow {
        animation: pulse-glow 3s ease-in-out infinite;
      }
      
      .animate-line-expand-delayed {
        animation: line-expand-delayed 1.5s ease-out 1s forwards;
      }
      
      .animate-fade-in-up {
        animation: fade-in-up 1s ease-out forwards;
        opacity: 0;
      }
      
      .animate-icon-float {
        animation: icon-float 3s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
  }
}
